import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, ChevronUp } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import './Pricing.css';

const plans = [
  {
    name: 'Free',
    price: { monthly: '₹0', annual: '₹0' },
    period: { monthly: 'forever', annual: 'forever' },
    desc: 'Perfect to explore and get started',
    features: ['20 credits on signup', 'Create 2D floor plans', '2-3 designs in 3D', 'One cost breakdown', 'One free consultation call', 'Browse 100+ templates', 'Community support'],
    cta: 'Start Free',
    highlighted: false,
  },
  {
    name: 'Pro',
    price: { monthly: '₹2,999', annual: '₹29,990' },
    period: { monthly: '/month', annual: '/year' },
    savings: 'Save ₹5,998/year',
    desc: 'For homeowners serious about building',
    badge: 'Most Popular',
    features: ['300 credits/month', 'Unlimited 3D views & designs', '10-20+ design variations', 'Virtual 360° walkthroughs', 'Detailed itemized costs', 'MEP planning with clash detection', 'Downloadable blueprints (PDF & DWG)', 'Vastu compliance checker', '2 architect video calls/month', '10% discount on construction', 'Priority support'],
    cta: 'Get Pro',
    highlighted: true,
  },
  {
    name: 'Business',
    price: { monthly: '₹19,999', annual: '₹1,99,990' },
    period: { monthly: '/month', annual: '/year' },
    savings: 'Save ₹39,998/year',
    desc: 'For builders, developers & architects',
    features: ['2,000 credits/month', 'Everything in Pro', 'Unlimited team members', 'White-label option', 'API access', 'Dedicated account manager', '15% discount on construction', 'Custom integrations', 'SLA guarantee'],
    cta: 'Contact Sales',
    highlighted: false,
  },
];

const creditCosts = [
  { feature: '2D Floor Plan', credits: 2 },
  { feature: '3D View', credits: 5 },
  { feature: 'Cost Breakdown', credits: 8 },
  { feature: 'MEP Planning', credits: 12 },
  { feature: 'Virtual Walkthrough', credits: 15 },
  { feature: 'Blueprint Export', credits: 10 },
  { feature: 'Vastu Check', credits: 5 },
  { feature: 'AI Interior Design', credits: 8 },
  { feature: 'Architect Consultation', credits: 20 },
];

const featureComparison = [
  { feature: '3D Design Studio', free: '3 designs', pro: 'Unlimited', biz: 'Unlimited' },
  { feature: 'Cost Estimation', free: '1 report', pro: 'Unlimited detailed', biz: 'Unlimited + API' },
  { feature: 'Virtual Walkthroughs', free: '-', pro: 'Unlimited', biz: 'Unlimited' },
  { feature: 'MEP Planning', free: '-', pro: 'Included', biz: 'Included' },
  { feature: 'Vastu Checker', free: '-', pro: 'Included', biz: 'Included' },
  { feature: 'Blueprint Export', free: '-', pro: 'PDF & DWG', biz: 'PDF & DWG + API' },
  { feature: 'Architect Calls', free: '1 free call', pro: '2/month', biz: 'Unlimited' },
  { feature: 'Construction Discount', free: '-', pro: '10%', biz: '15%' },
  { feature: 'Team Members', free: '1', pro: '3', biz: 'Unlimited' },
  { feature: 'Support', free: 'Community', pro: 'Priority', biz: 'Dedicated manager' },
];

const pricingFAQs = [
  { q: 'Is the free plan really free?', a: 'Yes, completely free. You get 20 credits on signup - enough to create your first floor plan, see it in 3D, and get a cost estimate. No credit card required.' },
  { q: 'Can I cancel anytime?', a: 'Yes. Monthly plans can be cancelled anytime. Annual plans can be cancelled with a full refund within the first 30 days.' },
  { q: 'What happens when I run out of credits?', a: 'You can purchase additional credit packs starting at ₹499 for 50 credits. Pro and Business plans also get monthly credit refills.' },
  { q: 'Is Pro really worth ₹2,999/month?', a: 'Absolutely. A single architect consultation costs ₹5,000-₹15,000. With Pro, you get 2 video calls/month plus unlimited 3D designs, cost estimates, and MEP planning - saving ₹50,000+ in architect and planning fees.' },
  { q: 'Do construction discounts stack with the plan?', a: 'The construction discount applies on top of Sculpit\'s already competitive pricing. Pro members save 10% and Business members save 15% on the total construction cost.' },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button className="faq-item__q" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && <div className="faq-item__a"><p>{answer}</p></div>}
    </div>
  );
}

export default function Pricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <div className="pricing-page">
      <section className="pricing-hero section-dark">
        <div className="pricing-hero__bg">
          <div className="pricing-hero__orb pricing-hero__orb--1" />
          <div className="pricing-hero__orb pricing-hero__orb--2" />
        </div>
        <div className="container pricing-hero__content">
          <AnimatedSection>
            <span className="label label-teal">Honest Pricing</span>
            <h1>Start Free. <span className="text-gradient">Upgrade When Ready.</span></h1>
            <p className="pricing-hero__subtitle">
              No hidden fees. No surprises. Just honest pricing for honest home building.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Toggle */}
      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="pricing-toggle">
              <span className={!annual ? 'pricing-toggle__active' : ''}>Monthly</span>
              <button className="pricing-toggle__switch" onClick={() => setAnnual(!annual)}>
                <div className={`pricing-toggle__thumb ${annual ? 'pricing-toggle__thumb--right' : ''}`} />
              </button>
              <span className={annual ? 'pricing-toggle__active' : ''}>
                Annual <span className="pricing-toggle__save">Save 17%</span>
              </span>
            </div>
          </AnimatedSection>

          <div className="pricing-cards">
            {plans.map((plan, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <div className={`pricing-card-full card-premium ${plan.highlighted ? 'pricing-card-full--popular' : ''}`}>
                  {plan.badge && <div className="pricing-card__badge">{plan.badge}</div>}
                  <h3>{plan.name}</h3>
                  <p className="pricing-card-full__desc">{plan.desc}</p>
                  <div className="pricing-card-full__price">
                    <span className="pricing-card-full__amount">{annual ? plan.price.annual : plan.price.monthly}</span>
                    <span className="pricing-card-full__period">{annual ? plan.period.annual : plan.period.monthly}</span>
                  </div>
                  {annual && plan.savings && (
                    <span className="pricing-card-full__savings">{plan.savings}</span>
                  )}
                  <ul className="pricing-card-full__features">
                    {plan.features.map((f, j) => (
                      <li key={j}><Check size={16} /> {f}</li>
                    ))}
                  </ul>
                  <Link
                    to={plan.name === 'Business' ? '/contact' : '/pricing'}
                    className={`btn ${plan.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ width: '100%' }}
                  >
                    {plan.cta} <ArrowRight size={18} />
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Credit Costs */}
      <section className="section section-gray">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Credit System</span>
              <h2>How Credits Work</h2>
              <p>Credits are the currency of Sculpit. Each action costs a specific number of credits.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="credit-grid">
              {creditCosts.map((item, i) => (
                <div key={i} className="credit-item card-premium">
                  <span className="credit-item__feature">{item.feature}</span>
                  <span className="credit-item__cost">{item.credits} credits</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <h2>Detailed Comparison</h2>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="pricing-comparison-wrap glass-card">
              <table className="pricing-comparison">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Free</th>
                    <th className="pricing-comparison__pop">Pro</th>
                    <th>Business</th>
                  </tr>
                </thead>
                <tbody>
                  {featureComparison.map((row, i) => (
                    <tr key={i}>
                      <td>{row.feature}</td>
                      <td>{row.free}</td>
                      <td className="pricing-comparison__pop">{row.pro}</td>
                      <td>{row.biz}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ROI */}
      <section className="section section-gray">
        <div className="container">
          <AnimatedSection>
            <div className="roi-card card-premium">
              <h3>Is Pro Worth It?</h3>
              <div className="roi-grid">
                <div className="roi-item">
                  <span className="roi-item__label">Architect consultation (2 calls)</span>
                  <span className="roi-item__val roi-item__val--old">₹10,000+</span>
                </div>
                <div className="roi-item">
                  <span className="roi-item__label">3D visualization</span>
                  <span className="roi-item__val roi-item__val--old">₹15,000+</span>
                </div>
                <div className="roi-item">
                  <span className="roi-item__label">MEP planning</span>
                  <span className="roi-item__val roi-item__val--old">₹20,000+</span>
                </div>
                <div className="roi-item">
                  <span className="roi-item__label">Blueprint preparation</span>
                  <span className="roi-item__val roi-item__val--old">₹10,000+</span>
                </div>
                <div className="roi-total">
                  <span>Traditional cost</span>
                  <span className="roi-total__val roi-total__val--old">₹55,000+</span>
                </div>
                <div className="roi-total roi-total--new">
                  <span>Sculpit Pro</span>
                  <span className="roi-total__val roi-total__val--new">₹2,999/mo</span>
                </div>
              </div>
              <p className="roi-note">That's a saving of <strong>₹50,000+</strong> on planning costs alone.</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <h2>Pricing FAQ</h2>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <div className="faq-list">
              {pricingFAQs.map((faq, i) => (
                <FAQItem key={i} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section section-dark" style={{ textAlign: 'center' }}>
        <div className="container">
          <AnimatedSection>
            <h2>Start Building Your Dream Home Today</h2>
            <p className="cta-subtitle">
              No credit card needed. 20 free credits to get started.
            </p>
            <Link to="/pricing" className="btn btn-primary btn-lg">
              Start Designing Free <ArrowRight size={18} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
